import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { riskManagementAPI } from '../services/api';
import { toast } from 'sonner';
import {
  ShieldAlert,
  Download,
  Upload,
  FileText,
  Calendar,
  User,
  Clock,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Eye,
  Trash2,
  Route,
  ChevronRight,
  History,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RiskManagementPage = () => {
  const { user, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('baseline');
  const [loading, setLoading] = useState(true);
  
  // Baseline state
  const [baselineAssessments, setBaselineAssessments] = useState([]);
  const [currentBaseline, setCurrentBaseline] = useState(null);
  const [showBaselineUpload, setShowBaselineUpload] = useState(false);
  const [baselineFile, setBaselineFile] = useState(null);
  const [baselineForm, setBaselineForm] = useState({
    assessment_date: new Date().toISOString().split('T')[0],
    assessor_name: '',
    notes: ''
  });
  
  // Route state
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [routeAssessments, setRouteAssessments] = useState([]);
  const [showRouteUpload, setShowRouteUpload] = useState(false);
  const [showRouteHistory, setShowRouteHistory] = useState(false);
  const [routeFile, setRouteFile] = useState(null);
  const [routeForm, setRouteForm] = useState({
    route_name: '',
    assessment_date: new Date().toISOString().split('T')[0],
    assessor_name: '',
    notes: ''
  });

  const [statistics, setStatistics] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [baselineRes, routesRes, statsRes] = await Promise.all([
        riskManagementAPI.listBaseline(),
        riskManagementAPI.getRoutes(),
        riskManagementAPI.getStatistics(),
      ]);
      
      setBaselineAssessments(baselineRes.data || []);
      setCurrentBaseline(baselineRes.data?.find(a => a.is_active) || null);
      setRoutes(routesRes.data || []);
      setStatistics(statsRes.data || {});
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load risk management data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getStatusBadge = (status, label) => {
    const statusConfig = {
      ok: { color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
      attention: { color: 'bg-blue-100 text-blue-800', icon: Clock },
      warning: { color: 'bg-amber-100 text-amber-800', icon: AlertCircle },
      critical: { color: 'bg-red-100 text-red-800', icon: AlertTriangle },
      overdue: { color: 'bg-red-600 text-white', icon: AlertTriangle },
      missing: { color: 'bg-slate-100 text-slate-800', icon: AlertCircle },
    };
    const config = statusConfig[status] || statusConfig.missing;
    const Icon = config.icon;
    
    return (
      <Badge variant="outline" className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  // Baseline handlers
  const handleBaselineUpload = async () => {
    if (!baselineFile) {
      toast.error('Please select a file to upload');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', baselineFile);
      formData.append('assessment_date', baselineForm.assessment_date);
      formData.append('assessor_name', baselineForm.assessor_name);
      formData.append('notes', baselineForm.notes || '');
      formData.append('user_id', user?.id || '');
      formData.append('user_name', user?.name || '');
      
      await riskManagementAPI.uploadBaseline(formData);
      toast.success('Baseline assessment uploaded successfully');
      setShowBaselineUpload(false);
      setBaselineFile(null);
      setBaselineForm({
        assessment_date: new Date().toISOString().split('T')[0],
        assessor_name: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error uploading baseline:', error);
      toast.error('Failed to upload baseline assessment');
    }
  };

  const handleDownloadTemplate = () => {
    riskManagementAPI.downloadBaselineTemplate();
  };

  const handleDownloadBaseline = (assessmentId) => {
    riskManagementAPI.downloadBaseline(assessmentId);
  };

  // Route handlers
  const handleRouteUpload = async () => {
    if (!routeFile || !routeForm.route_name) {
      toast.error('Please select a file and route');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', routeFile);
      formData.append('route_name', routeForm.route_name);
      formData.append('assessment_date', routeForm.assessment_date);
      formData.append('assessor_name', routeForm.assessor_name);
      formData.append('notes', routeForm.notes || '');
      formData.append('user_id', user?.id || '');
      formData.append('user_name', user?.name || '');
      
      await riskManagementAPI.uploadRouteAssessment(formData);
      toast.success('Route assessment uploaded successfully');
      setShowRouteUpload(false);
      setRouteFile(null);
      setRouteForm({
        route_name: '',
        assessment_date: new Date().toISOString().split('T')[0],
        assessor_name: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error uploading route assessment:', error);
      toast.error('Failed to upload route assessment');
    }
  };

  const handleViewRouteHistory = async (routeName) => {
    try {
      const response = await riskManagementAPI.getRouteAssessments(routeName);
      setRouteAssessments(response.data || []);
      setSelectedRoute(routeName);
      setShowRouteHistory(true);
    } catch (error) {
      console.error('Error fetching route history:', error);
      toast.error('Failed to load route history');
    }
  };

  const handleDownloadRoute = (assessmentId) => {
    riskManagementAPI.downloadRoute(assessmentId);
  };

  const handleDeleteBaseline = async (assessmentId) => {
    if (!window.confirm('Delete this baseline risk assessment? This will remove the file and cannot be undone.')) return;
    try {
      await riskManagementAPI.deleteBaseline(assessmentId);
      toast.success('Assessment deleted');
      fetchData();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handleDeleteRoute = async (assessmentId) => {
    if (!window.confirm('Delete this route risk assessment? This will remove the file and cannot be undone.')) return;
    try {
      await riskManagementAPI.deleteRoute(assessmentId);
      toast.success('Assessment deleted');
      setRouteAssessments(prev => prev.filter(a => a.id !== assessmentId));
      fetchData();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handleExportBaseline = () => {
    riskManagementAPI.exportBaseline();
  };

  const handleExportRoutes = () => {
    riskManagementAPI.exportRoutes();
  };

  return (
    <div className="space-y-6" data-testid="risk-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Risk Management</h1>
          <p className="text-slate-500">Baseline Risk Analysis & Route Risk Assessments</p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Baseline Status</p>
              <p className={`text-lg font-bold ${
                statistics.baseline?.status === 'ok' ? 'text-emerald-600' :
                statistics.baseline?.status === 'critical' || statistics.baseline?.status === 'overdue' ? 'text-red-600' :
                'text-amber-600'
              }`}>
                {statistics.baseline?.has_assessment ? 
                  (statistics.baseline?.days_until_review > 0 ? 
                    `${statistics.baseline?.days_until_review} days` : 'Overdue') 
                  : 'Missing'}
              </p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Routes</p>
              <p className="text-2xl font-bold">{statistics.routes?.total || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Routes Assessed</p>
              <p className="text-2xl font-bold text-emerald-600">{statistics.routes?.with_assessment || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Routes Missing</p>
              <p className="text-2xl font-bold text-red-600">{statistics.routes?.missing || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Routes Warning</p>
              <p className="text-2xl font-bold text-amber-600">{statistics.routes?.warning || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-teal-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Routes OK</p>
              <p className="text-2xl font-bold text-teal-600">{statistics.routes?.ok || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="baseline" className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4" />
            Baseline Risk Analysis
          </TabsTrigger>
          <TabsTrigger value="routes" className="flex items-center gap-2">
            <Route className="w-4 h-4" />
            Route Risk Assessment
          </TabsTrigger>
        </TabsList>

        {/* Baseline Tab */}
        <TabsContent value="baseline" className="space-y-4">
          {/* Actions */}
          <div className="flex gap-2 flex-wrap">
            <Button onClick={handleDownloadTemplate} variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
            <Button onClick={() => setShowBaselineUpload(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Upload Assessment
            </Button>
            <Button onClick={handleExportBaseline} variant="outline">
              <FileText className="w-4 h-4 mr-2" />
              Export to Excel
            </Button>
          </div>

          {/* Current Active Assessment */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-blue-500" />
                Current Active Baseline Assessment
              </CardTitle>
            </CardHeader>
            <CardContent>
              {currentBaseline ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Assessment Date</p>
                      <p className="font-medium">{currentBaseline.assessment_date}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Assessor</p>
                      <p className="font-medium">{currentBaseline.assessor_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Review Status</p>
                      {getStatusBadge(currentBaseline.review_status, currentBaseline.review_label)}
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Document</p>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="link" 
                          className="p-0 h-auto"
                          onClick={() => handleDownloadBaseline(currentBaseline.id)}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          {currentBaseline.filename}
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Delete"
                            onClick={() => handleDeleteBaseline(currentBaseline.id)}
                            data-testid="delete-current-baseline"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  {currentBaseline.notes && (
                    <div>
                      <p className="text-sm text-slate-500">Notes</p>
                      <p className="text-sm">{currentBaseline.notes}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <ShieldAlert className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-500">No baseline assessment uploaded yet</p>
                  <Button 
                    variant="outline" 
                    className="mt-2"
                    onClick={() => setShowBaselineUpload(true)}
                  >
                    Upload First Assessment
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Historic Assessments */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5 text-slate-500" />
                Assessment History
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Assessment Date</TableHead>
                    <TableHead>Assessor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead>Document</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {baselineAssessments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-4 text-slate-500">
                        No assessments found
                      </TableCell>
                    </TableRow>
                  ) : (
                    baselineAssessments.map((assessment) => (
                      <TableRow key={assessment.id} className={assessment.is_active ? 'bg-blue-50' : ''}>
                        <TableCell>{assessment.assessment_date}</TableCell>
                        <TableCell>{assessment.assessor_name}</TableCell>
                        <TableCell>
                          <Badge variant={assessment.is_active ? 'default' : 'secondary'}>
                            {assessment.is_active ? 'Active' : 'Historic'}
                          </Badge>
                        </TableCell>
                        <TableCell>{assessment.created_at?.split('T')[0]}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadBaseline(assessment.id)}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Delete"
                                onClick={() => handleDeleteBaseline(assessment.id)}
                                data-testid={`delete-baseline-${assessment.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Routes Tab */}
        <TabsContent value="routes" className="space-y-4">
          {/* Actions */}
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setShowRouteUpload(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Upload Route Assessment
            </Button>
            <Button onClick={handleExportRoutes} variant="outline">
              <FileText className="w-4 h-4 mr-2" />
              Export to Excel
            </Button>
          </div>

          {/* Routes Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Route className="w-5 h-5 text-purple-500" />
                Route Risk Assessments
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Route</TableHead>
                    <TableHead>Assessment Status</TableHead>
                    <TableHead>Assessment Date</TableHead>
                    <TableHead>Assessor</TableHead>
                    <TableHead>Review Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {routes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <Route className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                        <p className="text-slate-500">No routes found</p>
                        <p className="text-sm text-slate-400">Routes will appear here once planning records are created</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    routes.map((route) => (
                      <TableRow 
                        key={route.route_name}
                        className={
                          route.review_status === 'missing' || route.review_status === 'critical' || route.review_status === 'overdue' 
                            ? 'bg-red-50' 
                            : route.review_status === 'warning' 
                              ? 'bg-amber-50' 
                              : ''
                        }
                      >
                        <TableCell className="font-medium">{route.route_name}</TableCell>
                        <TableCell>
                          {route.has_assessment ? (
                            <Badge className="bg-emerald-100 text-emerald-800">Assessed</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">Missing</Badge>
                          )}
                        </TableCell>
                        <TableCell>{route.assessment_date || '-'}</TableCell>
                        <TableCell>{route.assessor_name || '-'}</TableCell>
                        <TableCell>
                          {getStatusBadge(route.review_status, route.review_label)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setRouteForm({ ...routeForm, route_name: route.route_name });
                                setShowRouteUpload(true);
                              }}
                              title="Upload Assessment"
                            >
                              <Upload className="w-4 h-4" />
                            </Button>
                            {route.has_assessment && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewRouteHistory(route.route_name)}
                                title="View History"
                              >
                                <History className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Baseline Upload Dialog */}
      <Dialog open={showBaselineUpload} onOpenChange={setShowBaselineUpload}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Baseline Risk Assessment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Assessment Date *</Label>
              <Input
                type="date"
                value={baselineForm.assessment_date}
                onChange={(e) => setBaselineForm({ ...baselineForm, assessment_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Assessor Name *</Label>
              <Input
                value={baselineForm.assessor_name}
                onChange={(e) => setBaselineForm({ ...baselineForm, assessor_name: e.target.value })}
                placeholder="Enter assessor name"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={baselineForm.notes}
                onChange={(e) => setBaselineForm({ ...baselineForm, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={3}
              />
            </div>
            <div>
              <Label>Assessment File *</Label>
              <Input
                type="file"
                accept=".xlsx,.xls,.pdf,.doc,.docx"
                onChange={(e) => setBaselineFile(e.target.files?.[0] || null)}
              />
              <p className="text-xs text-slate-500 mt-1">
                Upload the completed baseline risk assessment document
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBaselineUpload(false)}>
              Cancel
            </Button>
            <Button onClick={handleBaselineUpload} disabled={!baselineFile || !baselineForm.assessor_name}>
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Route Upload Dialog */}
      <Dialog open={showRouteUpload} onOpenChange={setShowRouteUpload}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Route Risk Assessment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Route *</Label>
              <Input
                value={routeForm.route_name}
                onChange={(e) => setRouteForm({ ...routeForm, route_name: e.target.value })}
                placeholder="Enter route name"
                list="route-list"
              />
              <datalist id="route-list">
                {routes.map((r) => (
                  <option key={r.route_name} value={r.route_name} />
                ))}
              </datalist>
            </div>
            <div>
              <Label>Assessment Date *</Label>
              <Input
                type="date"
                value={routeForm.assessment_date}
                onChange={(e) => setRouteForm({ ...routeForm, assessment_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Assessor Name *</Label>
              <Input
                value={routeForm.assessor_name}
                onChange={(e) => setRouteForm({ ...routeForm, assessor_name: e.target.value })}
                placeholder="Enter assessor name"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={routeForm.notes}
                onChange={(e) => setRouteForm({ ...routeForm, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={3}
              />
            </div>
            <div>
              <Label>Assessment File *</Label>
              <Input
                type="file"
                accept=".xlsx,.xls,.pdf,.doc,.docx"
                onChange={(e) => setRouteFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRouteUpload(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRouteUpload} 
              disabled={!routeFile || !routeForm.route_name || !routeForm.assessor_name}
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Route History Dialog */}
      <Dialog open={showRouteHistory} onOpenChange={setShowRouteHistory}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Assessment History: {selectedRoute}</DialogTitle>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Assessment Date</TableHead>
                <TableHead>Assessor</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Review Status</TableHead>
                <TableHead>Document</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {routeAssessments.map((assessment) => (
                <TableRow key={assessment.id} className={assessment.is_active ? 'bg-blue-50' : ''}>
                  <TableCell>{assessment.assessment_date}</TableCell>
                  <TableCell>{assessment.assessor_name}</TableCell>
                  <TableCell>
                    <Badge variant={assessment.is_active ? 'default' : 'secondary'}>
                      {assessment.is_active ? 'Active' : 'Historic'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(assessment.review_status, assessment.review_label)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadRoute(assessment.id)}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Delete"
                          onClick={() => handleDeleteRoute(assessment.id)}
                          data-testid={`delete-route-${assessment.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RiskManagementPage;
